import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { StatusBadge } from '@/components/StatusBadge';
import { Search, Filter, FileSpreadsheet, Layers, Download, MapPin, Package, Calendar, ChevronLeft, ChevronRight, Eye, Trash2, Phone, Pencil, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const ZONAS = ['', 'Zona Golf', 'Monte Pilar', 'Majadahonda Centro'];
const ESTADOS = ['', 'Notificada', 'Asignada', 'En Ruta', 'Recogida', 'Completada', 'Cancelada'];
const TIPOS = ['', 'Muebles/Enseres', 'Electrodomésticos'];

export default function AdminSolicitudes() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [filters, setFilters] = useState({ zona: '', estado: '', tipo: '', search: '', fecha_desde: '', fecha_hasta: '', telefono: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editSol, setEditSol] = useState(null);        // solicitud being edited
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [newItem, setNewItem] = useState('');

  const openEdit = (sol) => {
    setEditSol(sol);
    setEditForm({
      nombre_completo: sol.nombre_completo || '',
      email: sol.email || '',
      telefono: sol.telefono || '',
      direccion_completa: sol.direccion_completa || '',
      zona: sol.zona || '',
      fecha_preferida: sol.fecha_preferida?.slice(0, 10) || '',
      tipo_enseres: sol.tipo_enseres || '',
      enseres_lista: Array.isArray(sol.enseres_lista) ? [...sol.enseres_lista] : [],
      detalles: sol.detalles || '',
    });
    setNewItem('');
  };

  const addItem = () => {
    const v = newItem.trim();
    if (!v) return;
    setEditForm(f => ({ ...f, enseres_lista: [...f.enseres_lista, v] }));
    setNewItem('');
  };

  const removeItem = (idx) => {
    setEditForm(f => ({ ...f, enseres_lista: f.enseres_lista.filter((_, i) => i !== idx) }));
  };

  const saveEdit = async () => {
    if (!editForm.fecha_preferida) { toast.error('La fecha preferida es obligatoria'); return; }
    if (!editForm.tipo_enseres.trim()) { toast.error('Los enseres no pueden estar vacíos'); return; }
    setEditSaving(true);
    try {
      await axios.patch(
        `${API}/admin/solicitudes/${editSol.id}`,
        {
          nombre_completo: editForm.nombre_completo,
          email: editForm.email,
          telefono: editForm.telefono,
          direccion_completa: editForm.direccion_completa,
          zona: editForm.zona,
          fecha_preferida: editForm.fecha_preferida,
          tipo_enseres: editForm.tipo_enseres,
          enseres_lista: editForm.enseres_lista,
          detalles: editForm.detalles,
        },
        { headers: authHeaders() }
      );
      toast.success('Solicitud actualizada correctamente');
      setEditSol(null);
      fetchSolicitudes(page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar cambios');
    } finally {
      setEditSaving(false);
    }
  };

  const fetchSolicitudes = async (pg = 1, flt = filters) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: 20 };
      if (flt.zona) params.zona = flt.zona;
      if (flt.estado) params.estado = flt.estado;
      if (flt.tipo) params.tipo = flt.tipo;
      if (flt.search) params.search = flt.search;
      if (flt.fecha_desde) params.fecha_desde = flt.fecha_desde;
      if (flt.fecha_hasta) params.fecha_hasta = flt.fecha_hasta;
      if (flt.telefono) params.telefono = flt.telefono;
      const resp = await axios.get(`${API}/admin/solicitudes`, { headers: authHeaders(), params });
      setSolicitudes(resp.data.solicitudes);
      setTotal(resp.data.total);
      setPages(resp.data.pages);
      setPage(pg);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSolicitudes(1); }, []);

  const applyFilters = () => fetchSolicitudes(1, filters);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === solicitudes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(solicitudes.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    setShowDeleteModal(false);
    try {
      const resp = await axios.delete(`${API}/admin/solicitudes`, {
        headers: authHeaders(),
        data: { ids: Array.from(selectedIds) },
      });
      toast.success(resp.data.message);
      fetchSolicitudes(1);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al eliminar';
      toast.error(typeof msg === 'string' ? msg : 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const body = {
        format,
        zona: filters.zona || null,
        estado: filters.estado || null,
        fecha_desde: filters.fecha_desde || null,
        fecha_hasta: filters.fecha_hasta || null,
      };
      const resp = await axios.post(`${API}/admin/export`, body, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const ext = format === 'xlsx' ? 'xlsx' : format === 'shp' ? 'zip' : 'gpkg';
      const name = `enseres_majadahonda.${ext}`;
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportación ${format.toUpperCase()} completada`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al exportar';
      toast.error(typeof msg === 'string' ? msg : 'Error al exportar');
    } finally {
      setExporting('');
    }
  };

  return (
    <>
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Solicitudes</h1>
            <p className="text-sm text-slate-500">{total} total</p>
          </div>
          <div className="flex gap-2 items-center">
            {selectedIds.size > 0 && (
              <button
                onClick={() => setShowDeleteModal(true)}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
                data-testid="admin-bulk-delete-button"
              >
                <Trash2 size={14} />
                {deleting ? 'Eliminando...' : `Eliminar (${selectedIds.size})`}
              </button>
            )}
            <button
              onClick={() => handleExport('xlsx')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              data-testid="admin-export-xlsx-button"
            >
              <FileSpreadsheet size={15} className="text-[#009940]" />
              {exporting === 'xlsx' ? 'Exportando...' : 'XLSX'}
            </button>
            <button
              onClick={() => handleExport('shp')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              data-testid="admin-export-shp-button"
            >
              <Layers size={15} className="text-[#374B67]" />
              {exporting === 'shp' ? 'Exportando...' : 'SHP'}
            </button>
            <button
              onClick={() => handleExport('gpkg')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              data-testid="admin-export-gpkg-button"
            >
              <Download size={15} className="text-[#69C7EA]" />
              {exporting === 'gpkg' ? 'Exportando...' : 'GPKG'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div className="col-span-2 sm:col-span-1 lg:col-span-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && applyFilters()}
                  placeholder="Ticket, nombre..."
                  className="w-full border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={filters.telefono}
                onChange={e => setFilters(f => ({ ...f, telefono: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
                placeholder="Teléfono..."
                className="w-full border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                data-testid="admin-filter-telefono"
              />
            </div>
            <select
              value={filters.zona}
              onChange={e => setFilters(f => ({ ...f, zona: e.target.value }))}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
              data-testid="admin-filter-zona"
            >
              {ZONAS.map(z => <option key={z} value={z}>{z || 'Todas las zonas'}</option>)}
            </select>
            <select
              value={filters.estado}
              onChange={e => setFilters(f => ({ ...f, estado: e.target.value }))}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
              data-testid="admin-filter-estado"
            >
              {ESTADOS.map(e => <option key={e} value={e}>{e || 'Todos los estados'}</option>)}
            </select>
            <select
              value={filters.tipo}
              onChange={e => setFilters(f => ({ ...f, tipo: e.target.value }))}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
            >
              {TIPOS.map(t => <option key={t} value={t}>{t || 'Todos los tipos'}</option>)}
            </select>
            <button
              onClick={applyFilters}
              className="flex items-center justify-center gap-1.5 bg-[#374B67] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#374B67]/90 transition-colors"
              data-testid="admin-filter-apply-button"
            >
              <Filter size={14} /> Filtrar
            </button>
          </div>
        </div>

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 size={18} className="text-red-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Confirmar eliminación</h3>
                  <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-5">
                Vas a eliminar <strong>{selectedIds.size} solicitud{selectedIds.size !== 1 ? 'es' : ''}</strong>.
                Las solicitudes asignadas a rutas serán desasignadas automáticamente.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
                  data-testid="admin-confirm-delete-button"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#009940] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : solicitudes.length === 0 ? (
            <div className="text-center py-16">
              <Package size={40} className="text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No hay solicitudes con los filtros aplicados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === solicitudes.length && solicitudes.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 text-[#374B67] cursor-pointer"
                        data-testid="admin-select-all-checkbox"
                      />
                    </th>
                    {['Código', 'F. Preferida', 'Nombre', 'Teléfono', 'Calle', 'Tipo', 'Estado', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {solicitudes.map(s => (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(s.id) ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          className="rounded border-slate-300 text-[#374B67] cursor-pointer"
                          data-testid={`admin-select-checkbox-${s.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono font-medium text-[#374B67]">{s.ticket_code}</td>
                      <td className="px-4 py-3">
                        <span className="text-slate-700 font-medium">{s.fecha_preferida || <span className="text-slate-300 italic">-</span>}</span>
                        <span className="block text-xs text-slate-400">Creada: {s.fecha_peticion}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{s.nombre_completo || <span className="text-slate-300 italic">Anónimo</span>}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-slate-500 font-mono text-xs">
                          <Phone size={11} className="text-slate-400 shrink-0" />
                          {s.telefono || <span className="text-slate-300 italic">-</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const full = s.direccion_aprox || s.direccion_completa || '';
                          const parts = full.split(',').map(p => p.trim()).filter(Boolean);
                          const first = parts[0] || '';
                          // Nominatim sometimes returns "6, Barrio X, City" — detect bare number
                          // Also guard against raw GPS coords stored as address
                          const isNumber = /^\d+$/.test(first);
                          const isCoords = /^\d+\.\d+$/.test(first);
                          const display = isCoords
                            ? (s.zona || 'Ver detalle')
                            : isNumber && parts[1]
                              ? `${parts[1]} nº${first}`
                              : first;
                          return (
                            <>
                              <span className="text-slate-700 text-sm leading-snug">{display || <span className="text-slate-300 italic">—</span>}</span>
                              {s.zona && <span className="block text-xs text-slate-400 mt-0.5">{s.zona}</span>}
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{s.tipo_enseres}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.estado} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/solicitudes/${s.id}`}
                            className="flex items-center gap-1 text-[#374B67] hover:text-[#009940] font-medium transition-colors"
                            data-testid="admin-solicitud-detail-link"
                          >
                            <Eye size={14} /> Ver
                          </Link>
                          <button
                            onClick={() => openEdit(s)}
                            className="flex items-center gap-1 text-amber-600 hover:text-amber-800 font-medium transition-colors"
                            data-testid={`admin-solicitud-edit-btn-${s.id}`}
                          >
                            <Pencil size={13} /> Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Página {page} de {pages} · {total} total</span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchSolicitudes(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => fetchSolicitudes(page + 1)}
                  disabled={page === pages}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>

      {/* ── Edit Modal ──────────────────────────────────────────────────────── */}
      <Dialog open={!!editSol} onOpenChange={(o) => { if (!o) setEditSol(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-solicitud-modal">
          <DialogHeader>
            <DialogTitle className="text-[#374B67]">
              Editar solicitud — <span className="font-mono text-sm">{editSol?.ticket_code}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2 max-h-[70vh] overflow-y-auto pr-1">

            {/* ── Datos personales ─────────────────────────────────── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Datos del solicitante</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Nombre completo</Label>
                  <Input value={editForm.nombre_completo || ''} onChange={(e) => setEditForm(f => ({ ...f, nombre_completo: e.target.value }))} placeholder="Nombre y apellidos" className="rounded-xl" data-testid="edit-nombre-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Teléfono</Label>
                    <Input value={editForm.telefono || ''} onChange={(e) => setEditForm(f => ({ ...f, telefono: e.target.value }))} placeholder="+34 600 000 000" className="rounded-xl" data-testid="edit-telefono-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-700">Email</Label>
                    <Input value={editForm.email || ''} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder="correo@ejemplo.com" className="rounded-xl" data-testid="edit-email-input" />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Dirección ─────────────────────────────────────────── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Dirección de recogida</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Dirección completa</Label>
                  <Input value={editForm.direccion_completa || ''} onChange={(e) => setEditForm(f => ({ ...f, direccion_completa: e.target.value }))} placeholder="Calle, número, municipio..." className="rounded-xl" data-testid="edit-direccion-input" />
                  <p className="text-xs text-amber-600">Nota: editar la dirección no mueve el pin en el mapa. Para cambiar coordenadas el ciudadano debe crear una nueva solicitud.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Zona</Label>
                  <Input value={editForm.zona || ''} onChange={(e) => setEditForm(f => ({ ...f, zona: e.target.value }))} placeholder="Zona Golf, Centro..." className="rounded-xl" data-testid="edit-zona-input" />
                </div>
              </div>
            </div>

            {/* ── Recogida ──────────────────────────────────────────── */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Detalles de la recogida</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Fecha de recogida preferida</Label>
                  <Input type="date" value={editForm.fecha_preferida || ''} onChange={(e) => setEditForm(f => ({ ...f, fecha_preferida: e.target.value }))} className="rounded-xl" data-testid="edit-fecha-preferida-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Descripción de enseres</Label>
                  <Input value={editForm.tipo_enseres || ''} onChange={(e) => setEditForm(f => ({ ...f, tipo_enseres: e.target.value }))} placeholder="Ej: Sofá 3 plazas, nevera…" className="rounded-xl" data-testid="edit-tipo-enseres-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Lista de enseres</Label>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2 max-h-32 overflow-y-auto">
                    {editForm.enseres_lista?.length === 0 && <p className="text-xs text-slate-400 italic">Sin ítems</p>}
                    {editForm.enseres_lista?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-1.5">
                        <span className="flex-1 text-sm text-slate-700">{item}</span>
                        <button type="button" onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`edit-remove-item-${idx}`}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} placeholder="Ej: Sofá 2 plazas (x1)" className="rounded-xl flex-1 text-sm" data-testid="edit-new-item-input" />
                    <Button type="button" onClick={addItem} variant="outline" className="rounded-xl px-3" data-testid="edit-add-item-btn"><Plus size={15} /></Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Instrucciones de recogida</Label>
                  <Textarea value={editForm.detalles || ''} onChange={(e) => setEditForm(f => ({ ...f, detalles: e.target.value }))} placeholder="Portal, escalera, observaciones…" rows={2} className="rounded-xl resize-none text-sm" data-testid="edit-detalles-input" />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditSol(null)}
              className="rounded-xl"
              data-testid="edit-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving}
              className="rounded-xl bg-[#374B67] hover:bg-[#2A3B52] text-white"
              data-testid="edit-save-btn"
            >
              {editSaving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
